import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { QueueMetrics, ChartData } from '../types';
import { transcriptionQueueApi } from '../api/transcriptionQueueApi';

export type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

export interface MetricsState {
  timeRange: TimeRange;
  metrics: QueueMetrics[];
  queueDepthChart: ChartData[];
  processingRateChart: ChartData[];
  successRateChart: ChartData[];
  errorDistribution: Record<string, number>;
  performanceByCompany: Array<{
    company: string;
    avgProcessingTime: number;
    successRate: number;
    totalJobs: number;
  }>;
  loading: boolean;
  error: string | null;
  lastUpdate: string | null;
}

const initialState: MetricsState = {
  timeRange: '24h',
  metrics: [],
  queueDepthChart: [],
  processingRateChart: [],
  successRateChart: [],
  errorDistribution: {},
  performanceByCompany: [],
  loading: false,
  error: null,
  lastUpdate: null,
};

export const fetchMetrics = createAsyncThunk<
  {
    metrics: QueueMetrics[];
    queueDepthChart: ChartData[];
    processingRateChart: ChartData[];
    successRateChart: ChartData[];
    errorDistribution: Record<string, number>;
    performanceByCompany: Array<{
      company: string;
      avgProcessingTime: number;
      successRate: number;
      totalJobs: number;
    }>;
  },
  TimeRange
>('transcriptionMetrics/fetchMetrics', async (timeRange) => {
  const response = await transcriptionQueueApi.getMetrics(timeRange);
  return response;
});

export const fetchErrorDistribution = createAsyncThunk<
  Record<string, number>,
  TimeRange
>('transcriptionMetrics/fetchErrorDistribution', async (timeRange) => {
  const response = await transcriptionQueueApi.getErrorDistribution(timeRange);
  return response;
});

const metricsSlice = createSlice({
  name: 'transcriptionMetrics',
  initialState,
  reducers: {
    setTimeRange: (state, action: PayloadAction<TimeRange>) => {
      state.timeRange = action.payload;
    },
    updateMetricsFromWebSocket: (state, action: PayloadAction<QueueMetrics>) => {
      state.metrics.push(action.payload);
      
      if (state.metrics.length > 1000) {
        state.metrics = state.metrics.slice(-1000);
      }
      
      const newPoint = {
        timestamp: action.payload.timestamp,
        value: action.payload.queueDepth,
      };
      
      const queueDepthSeries = state.queueDepthChart.find((s) => s.label === 'Queue Depth');
      if (queueDepthSeries) {
        queueDepthSeries.series.push(newPoint);
        if (queueDepthSeries.series.length > 100) {
          queueDepthSeries.series.shift();
        }
      }
      
      state.lastUpdate = new Date().toISOString();
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.queueDepthChart = action.payload.queueDepthChart;
        state.processingRateChart = action.payload.processingRateChart;
        state.successRateChart = action.payload.successRateChart;
        state.errorDistribution = action.payload.errorDistribution;
        state.performanceByCompany = action.payload.performanceByCompany;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch metrics';
      })
      .addCase(fetchErrorDistribution.fulfilled, (state, action) => {
        state.errorDistribution = action.payload;
      });
  },
});

export const { setTimeRange, updateMetricsFromWebSocket, clearError } = metricsSlice.actions;

export default metricsSlice.reducer;